import { useQuery } from '@tanstack/react-query'
import LoadingScreen from 'components/Layout/LoadingScreen'
import type { Campaign } from 'models/campaign'
import { ContactsTable } from 'components/Tables/ContactsTable/ContactsTable'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import {
  CONTACTS_TABLE_KEY,
  getCampaignTableKey,
} from 'constants/tableQueryKeys'
import { ALLOWED_CONTACT_REQUEST_TYPES } from 'models/companies'
import { CAMPAIGN_DEFAULT_COLUMN_VISIBILITY } from 'components/Tables/ContactsTable/sequence/campaignDefaultColumns'
import apiService from 'services/api'
import { useGetCampaignCompanies } from './campaigns_api'
import { CampaignContactsZeroState } from './CampaignContactsZeroState'

interface Props {
  campaign: Campaign
  onSwitchToTargets: () => void
}

export function CampaignDashboardContactsTab({
  campaign,
  onSwitchToTargets,
}: Props) {
  const api = apiService()
  const { data: campaignCompanies, isLoading: isLoadingTargets } =
    useGetCampaignCompanies(campaign.id)
  const { data: contactsData, isLoading: isLoadingContacts } = useQuery({
    queryKey: ['campaign', campaign.id, 'contacts-count'],
    queryFn: () =>
      api.getContactsList({
        campaign: campaign.id,
        campaign_page: true,
        type: 'all',
        limit: 1,
        page: 1,
      }),
    staleTime: 1000 * 60 * 5,
  })
  const hasContactRequests = ALLOWED_CONTACT_REQUEST_TYPES.includes(
    campaign.type.slug as any
  )
  const targetCount = campaignCompanies?.total_count ?? 0
  const contactCount = contactsData?.count ?? 0

  if (isLoadingTargets || isLoadingContacts) {
    return <LoadingScreen />
  }

  if (targetCount === 0) {
    return (
      <CampaignContactsZeroState
        variant="assign-targets"
        hasContactRequests={hasContactRequests}
        onSwitchToTargets={onSwitchToTargets}
      />
    )
  }

  if (contactCount === 0) {
    return (
      <CampaignContactsZeroState
        variant="request-contacts"
        hasContactRequests={hasContactRequests}
        onSwitchToTargets={onSwitchToTargets}
      />
    )
  }

  return (
    <FBPageTableContainer>
      <ContactsTable
        disableUrlPagination
        campaignId={campaign.id}
        defaultColumnVisibility={CAMPAIGN_DEFAULT_COLUMN_VISIBILITY}
        tableKey={getCampaignTableKey(CONTACTS_TABLE_KEY, campaign.id)}
        filterIdentifierModifier={`_CAMPAIGN_V2_${campaign.id}`}
        baseFilters={{ campaign: campaign.id }}
      />
    </FBPageTableContainer>
  )
}
